import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API from '../../services/api';
import useToast from './useToast';

const authH      = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });
const isDemoMode = () => localStorage.getItem('adminMode') === 'demo';
const CATS  = ['general','women','business','leadership','healthcare','education','technology'];
const ICONS = ['👑','📊','🏥','🎓','🌱','💡','🌍','🚀','✨','🤝','💼','🧠','🔥','📈','⚡','🌟','💎','🏆'];
const EMPTY = { topic:'', icon:'📈', count:0, category:'general', active:true };

const DEMO_TRENDS = [
  { _id:'t1',  topic:'#WomenInLeadership',  icon:'👑', count:4200, category:'women',      active:true  },
  { _id:'t2',  topic:'#BusinessStrategy',   icon:'📊', count:3800, category:'business',   active:true  },
  { _id:'t3',  topic:'#HealthcareLeaders',  icon:'🏥', count:2900, category:'healthcare', active:true  },
  { _id:'t4',  topic:'#EducationFuture',    icon:'🎓', count:2500, category:'education',  active:true  },
  { _id:'t5',  topic:'#SustainableGrowth',  icon:'🌱', count:2200, category:'business',   active:true  },
  { _id:'t6',  topic:'#TechInnovation',     icon:'💡', count:3100, category:'technology', active:true  },
  { _id:'t7',  topic:'#GlobalLeadership',   icon:'🌍', count:3600, category:'leadership', active:true  },
  { _id:'t8',  topic:'#FounderJourneys',    icon:'🚀', count:2800, category:'business',   active:true  },
  { _id:'t9',  topic:'#WomenEntrepreneurs', icon:'✨', count:4100, category:'women',      active:true  },
  { _id:'t10', topic:'#CorporateCulture',   icon:'🤝', count:1900, category:'business',   active:false },
];

let demoTrendCounter = 300;

export default function AdminTrends() {
  const [trends,  setTrends]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(EMPTY);
  const [saving,  setSaving]  = useState(false);
  const { addToast, ToastContainer } = useToast();

  const fetchTrends = async () => {
    setLoading(true);
    if (isDemoMode()) {
      setTimeout(() => { setTrends([...DEMO_TRENDS]); setLoading(false); }, 400);
      return;
    }
    try {
      const r = await API.get('/trends', { headers: authH() });
      setTrends(r.data.data?.length ? r.data.data : [...DEMO_TRENDS]);
    } catch { setTrends([...DEMO_TRENDS]); }
    finally  { setLoading(false); }
  };

  useEffect(() => { fetchTrends(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit   = t  => { setEditing(t); setForm({ topic:t.topic, icon:t.icon, count:t.count, category:t.category, active:!!t.active }); setModal(true); };
  const closeModal = () => { setModal(false); setEditing(null); };
  const setF = (k,v) => setForm(f => ({ ...f, [k]:v }));

  const handleSave = async e => {
    e.preventDefault();
    if (!form.topic) return addToast('Topic name is required','error');
    if (isDemoMode()) {
      setSaving(true);
      await new Promise(r => setTimeout(r,500));
      if (editing) {
        setTrends(prev => prev.map(t => t._id===editing._id ? {...t,...form} : t));
        addToast('Trend updated! (demo)','success');
      } else {
        setTrends(prev => [{ ...form, _id:`demo-t${++demoTrendCounter}` }, ...prev]);
        addToast('Trend created! (demo)','success');
      }
      setSaving(false); closeModal(); return;
    }
    setSaving(true);
    try {
      if (editing) { await API.put(`/trends/${editing._id}`, form, { headers:authH() }); addToast('Updated!','success'); }
      else          { await API.post('/trends', form, { headers:authH() }); addToast('Created!','success'); }
      closeModal(); fetchTrends();
    } catch(err) { addToast(err.response?.data?.message||'Save failed','error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this trend?')) return;
    if (isDemoMode()) {
      setTrends(prev => prev.filter(t => t._id !== id));
      addToast('Deleted (demo)','success'); return;
    }
    try {
      await API.delete(`/trends/${id}`, { headers:authH() });
      addToast('Deleted','success');
      setTrends(prev => prev.filter(t => t._id !== id));
    } catch { addToast('Delete failed','error'); }
  };

  const toggleActive = async trend => {
    const updated = { ...trend, active: !trend.active };
    if (isDemoMode()) {
      setTrends(prev => prev.map(t => t._id===trend._id ? updated : t));
      addToast(updated.active ? 'Topic made active' : 'Topic hidden','info'); return;
    }
    try {
      await API.put(`/trends/${trend._id}`, updated, { headers:authH() });
      setTrends(prev => prev.map(t => t._id===trend._id ? updated : t));
    } catch { addToast('Update failed','error'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Trending Topics</h1>
          <p className="page-subtitle">
            {trends.filter(t=>t.active).length} active · {trends.filter(t=>!t.active).length} hidden
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openCreate}>+ New Topic</button>
        </div>
      </div>

      <div className="table-card">
        <div className="table-header">
          <span className="table-title">All Trends</span>
          <div className="table-controls">
            <span style={{ fontSize:'.76rem', color:'var(--white40)' }}>
              {trends.length} total topics
            </span>
          </div>
        </div>

        <div style={{ overflowX:'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width:48 }}>Icon</th>
                <th>Topic</th>
                <th>Category</th>
                <th>Count</th>
                <th>Visibility</th>
                <th style={{ width:110, textAlign:'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({length:5}).map((_,i)=>(
                  <tr key={i}>{[48,200,100,80,80,110].map((w,j)=>(
                    <td key={j}><div style={{ height:13, background:'var(--card2)', borderRadius:3, animation:'pulse 1.4s infinite' }}/></td>
                  ))}</tr>
                ))
              ) : trends.length === 0 ? (
                <tr><td colSpan={6}>
                  <div className="empty-state">
                    <div className="empty-title">No trends yet</div>
                    <div className="empty-sub">Create your first trending topic.</div>
                  </div>
                </td></tr>
              ) : trends.map(t => (
                <tr key={t._id}>
                  <td>
                    <div style={{ fontSize:'1.6rem', lineHeight:1, textAlign:'center' }}>{t.icon}</div>
                  </td>
                  <td>
                    <span style={{ fontWeight:600, color:'var(--white)', fontSize:'.88rem' }}>{t.topic}</span>
                  </td>
                  <td><span className="badge badge-gold">{t.category}</span></td>
                  <td style={{ fontVariantNumeric:'tabular-nums', color:'var(--white70)' }}>
                    {(t.count||0).toLocaleString()}
                  </td>
                  <td>
                    <button onClick={() => toggleActive(t)}
                      style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}>
                      <span className={`badge ${t.active?'badge-green':'badge-red'}`}>
                        {t.active ? '✓ Active' : '✕ Hidden'}
                      </span>
                    </button>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="act-edit"   onClick={() => openEdit(t)}>✏️</button>
                      <button className="act-delete" onClick={() => handleDelete(t._id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live Preview */}
      {trends.filter(t=>t.active).length > 0 && (
        <div className="table-card" style={{ marginTop:20 }}>
          <div className="table-header">
            <span className="table-title">Live Preview — as shown on site</span>
          </div>
          <div style={{ padding:'20px 24px', display:'flex', flexWrap:'wrap', gap:10 }}>
            {trends.filter(t=>t.active).map(t => (
              <div key={t._id} style={{
                display:'inline-flex', alignItems:'center', gap:8,
                padding:'9px 18px', borderRadius:'100px',
                background:'var(--card2)', border:'1px solid var(--border-lt)',
                fontSize:'.82rem', color:'var(--white70)',
              }}>
                <span>{t.icon}</span>
                {t.topic}
                <span style={{ fontSize:'.68rem', color:'var(--white40)', marginLeft:2 }}>
                  {t.count >= 1000 ? (t.count/1000).toFixed(1)+'k' : t.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Modal ── */}
      {modal && (
        <div className="modal-root">
          <div className="modal-backdrop" onClick={closeModal}/>
          <div className="modal-panel modal-center" style={{ maxWidth:500, height:'auto' }}>
            <div className="modal-head">
              <h2>{editing ? 'Edit Trend' : 'New Trend'}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              <form id="trend-form" onSubmit={handleSave}>
                <div className="form-grid cols-1" style={{ gap:18 }}>

                  <div className="form-group">
                    <label className="form-label">Topic Name <span className="req">*</span></label>
                    <input className="form-input" value={form.topic} required
                      onChange={e=>setF('topic',e.target.value)}
                      placeholder="#WomenInLeadership"/>
                    <span className="form-hint">Use hashtag format for best display</span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Icon</label>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
                      {ICONS.map(ic => (
                        <button key={ic} type="button" onClick={() => setF('icon', ic)}
                          style={{
                            padding:'6px 10px', borderRadius:4, fontSize:'1.1rem',
                            border:`1px solid ${form.icon===ic?'var(--gold)':'var(--border-lt)'}`,
                            background: form.icon===ic ? 'rgba(201,168,76,.12)' : 'var(--card2)',
                            cursor:'pointer', transition:'all .18s',
                          }}>
                          {ic}
                        </button>
                      ))}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:'1.8rem' }}>{form.icon}</span>
                      <input className="form-input" value={form.icon}
                        onChange={e=>setF('icon',e.target.value)}
                        placeholder="Paste any emoji"
                        style={{ maxWidth:160 }}/>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-select" value={form.category}
                      onChange={e=>setF('category',e.target.value)}>
                      {CATS.map(c=>(
                        <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Engagement Count</label>
                    <input className="form-input" type="number" min={0} value={form.count}
                      onChange={e=>setF('count',+e.target.value)}/>
                    <span className="form-hint">Shown as "1.2k" if ≥ 1000</span>
                  </div>

                  <div className="form-group">
                    <label className="form-check">
                      <input type="checkbox" checked={form.active}
                        onChange={e=>setF('active',e.target.checked)}/>
                      <span className="form-check-label">Visible on site</span>
                    </label>
                  </div>

                </div>
              </form>
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" form="trend-form" type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? '✓ Update' : '+ Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      <ToastContainer/>
    </div>
  );
}
